import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { Dropdown, AutoCompleteItem } from "@/components/Dropdown/Dropdown";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { SearchField } from "@/components/SearchField/SearchField";
import { useGetTtsVoicesQuery } from "@/pages/Private/redux/courses/courses.api";
import { TtsVoice } from "@/pages/Private/pages/Courses/schema/courses";

interface VoiceSelectModalProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (voiceId: string, voiceName: string) => void;
	selectedVoiceId: string;
	courseLanguage?: string;
}

function buildDropdownOptions(
	allLabel: string,
	values: (string | undefined)[],
	labelOf: (value?: string) => string
): AutoCompleteItem[] {
	const unique = Array.from(new Set(values.filter((v): v is string => !!v)));
	const options = unique
		.map((v) => ({ id: v, title: labelOf(v) }))
		.sort((a, b) => a.title.localeCompare(b.title));

	return [{ id: "", title: allLabel }, ...options];
}

interface VoiceListProps {
	filteredVoices: TtsVoice[];
	localSelected: string;
	playingId: string | null;
	onSelect: (id: string) => void;
	onPlay: (voice: TtsVoice) => void;
	ts: (key: string) => string;
	/** Provider values like `middle_aged` shown as words a person would use. */
	labelOf: (value?: string) => string;
}

function VoiceList({
	filteredVoices,
	localSelected,
	playingId,
	onSelect,
	onPlay,
	ts,
	labelOf,
}: VoiceListProps) {
	if (filteredVoices.length === 0) {
		return <p className="text-sm text-gray-500 py-4 text-center">{ts("noVoices")}</p>;
	}

	return (
		<div className="grid grid-cols-1 gap-2 max-h-[32vh] overflow-y-auto pr-1 custom-scrollbar">
			{filteredVoices.map((voice) => {
				const isSelected = localSelected === voice.voiceId;
				const isPlaying = playingId === voice.voiceId;
				const meta = [voice.gender, voice.age, voice.accent]
					.filter(Boolean)
					.map((value) => labelOf(value))
					.join(" · ");

				return (
					<button
						key={voice.voiceId}
						className={`w-full text-left px-4 py-3 rounded-lg border transition-colors flex items-center justify-between gap-3 ${
							isSelected
								? "border-accent bg-accent-tint"
								: "border-gray-200 bg-white hover:bg-gray-50"
						}`}
						type="button"
						onClick={() => onSelect(voice.voiceId)}
					>
						<div className="flex-1 min-w-0">
							<p
								className={`text-sm font-semibold truncate ${isSelected ? "text-brand-light" : "text-gray-900"}`}
							>
								{voice.name}
							</p>
							{meta && <p className="text-xs text-gray-500 truncate">{meta}</p>}
							{voice.category && (
								<span className="inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
									{labelOf(voice.category)}
								</span>
							)}
						</div>
						{voice.previewUrl && (
							<Button
								className="!w-9 !h-9 !p-0 flex-shrink-0"
								color={isPlaying ? ButtonColor.ACTION : ButtonColor.ACTION_SECONDARY}
								size={ButtonSize.XS}
								title={isPlaying ? "⏹" : "▶"}
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									onPlay(voice);
								}}
							/>
						)}
					</button>
				);
			})}
		</div>
	);
}

export default function VoiceSelectModal({
	isOpen,
	onClose,
	onConfirm,
	selectedVoiceId,
	courseLanguage,
}: VoiceSelectModalProps) {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`courses.tts.${key}`), [t]);

	/**
	 * The provider's attribute values — `middle_aged`, `narrative_story` — as words a
	 * person would use. Translated where known; a value the map has never seen falls back
	 * to the value with its underscores spaced out, so a new provider term is readable
	 * rather than raw.
	 */
	const labelOf = useCallback(
		(value?: string) => {
			if (!value) {
				return "";
			}

			const key = `courses.tts.values.${value}`;
			const translated = t(key);

			return translated === key
				? value.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())
				: translated;
		},
		[t]
	);

	// Callers hand the language in whatever shape they hold it — "EN" from the course
	// enum, "en" from the wizard. The option ids below are lowercase codes, and a value
	// that misses them leaves the dropdown looking unchosen, so normalise here.
	const [langFilter, setLangFilter] = useState<string>((courseLanguage ?? "").toLowerCase());
	const [genderFilter, setGenderFilter] = useState<string>("");
	const [ageFilter, setAgeFilter] = useState<string>("");
	const [useCaseFilter, setUseCaseFilter] = useState<string>("");
	const [categoryFilter, setCategoryFilter] = useState<string>("");
	const [searchQuery, setSearchQuery] = useState<string>("");
	const [localSelected, setLocalSelected] = useState<string>(selectedVoiceId);
	const [playingId, setPlayingId] = useState<string | null>(null);
	const audioRef = useRef<HTMLAudioElement | null>(null);

	const { data: voices = [] } = useGetTtsVoicesQuery(
		langFilter ? { language: langFilter } : undefined
	);

	const langOptions = useMemo<AutoCompleteItem[]>(
		() => [
			{ id: "", title: ts("allLanguages") },
			{ id: "multilingual", title: "Multilingual" },
			{ id: "de", title: "Deutsch" },
			// The code, not the name: the pre-selection compares against language codes,
			// and the server's filter takes either form.
			{ id: "en", title: "English" },
			{ id: "es", title: "Español" },
			{ id: "fr", title: "Français" },
			{ id: "ja", title: "日本語" },
			{ id: "zh", title: "中文" },
			{ id: "ko", title: "한국어" },
		],
		[ts]
	);

	const genderOptions = useMemo(
		() =>
			buildDropdownOptions(
				ts("allGenders"),
				voices.map((v) => v.gender),
				labelOf
			),
		[voices, ts, labelOf]
	);
	const ageOptions = useMemo(
		() =>
			buildDropdownOptions(
				ts("allAges"),
				voices.map((v) => v.age),
				labelOf
			),
		[voices, ts, labelOf]
	);
	const useCaseOptions = useMemo(
		() =>
			buildDropdownOptions(
				ts("allUseCases"),
				voices.map((v) => v.useCase),
				labelOf
			),
		[voices, ts, labelOf]
	);
	const categoryOptions = useMemo(
		() =>
			buildDropdownOptions(
				ts("allCategories"),
				voices.map((v) => v.category),
				labelOf
			),
		[voices, ts, labelOf]
	);

	const filteredVoices = useMemo<TtsVoice[]>(() => {
		return voices.filter((v) => {
			if (genderFilter && v.gender !== genderFilter) {
				return false;
			}

			if (ageFilter && v.age !== ageFilter) {
				return false;
			}

			if (useCaseFilter && v.useCase !== useCaseFilter) {
				return false;
			}

			if (categoryFilter && v.category !== categoryFilter) {
				return false;
			}

			if (searchQuery && !v.name.toLowerCase().includes(searchQuery.toLowerCase())) {
				return false;
			}

			return true;
		});
	}, [voices, genderFilter, ageFilter, useCaseFilter, categoryFilter, searchQuery]);

	const handleLangChange = useCallback((item: AutoCompleteItem) => {
		setLangFilter(item.id as string);
		setLocalSelected("");
	}, []);

	const handlePlay = useCallback(
		(voice: TtsVoice) => {
			if (!voice.previewUrl) {
				return;
			}

			if (playingId === voice.voiceId) {
				audioRef.current?.pause();
				setPlayingId(null);

				return;
			}

			if (audioRef.current) {
				audioRef.current.pause();
			}

			const audio = new Audio(voice.previewUrl);

			audioRef.current = audio;
			audio.play();
			setPlayingId(voice.voiceId);
			audio.onended = () => setPlayingId(null);
		},
		[playingId]
	);

	const handleConfirm = useCallback(() => {
		audioRef.current?.pause();
		const voice = filteredVoices.find((v) => v.voiceId === localSelected);
		const meta = voice ? [voice.gender, voice.accent].filter(Boolean).join(", ") : "";
		const label = voice ? (meta ? `${voice.name} — ${meta}` : voice.name) : localSelected;

		onConfirm(localSelected, label);
	}, [localSelected, filteredVoices, onConfirm]);

	const handleClose = useCallback(() => {
		audioRef.current?.pause();
		setPlayingId(null);
		onClose();
	}, [onClose]);

	return (
		<Modal
			disableSave={!localSelected}
			handleClose={handleClose}
			handleSave={handleConfirm}
			isOpened={isOpen}
			size="md"
			submitButtonText={ts("confirmVoice")}
			title={ts("selectVoice")}
		>
			<div className="space-y-4">
				{/* Six controls, so three columns is two even rows — two columns was three. */}
				<div className="grid grid-cols-3 gap-3">
					<Dropdown
						floating
						data={langOptions}
						handleSelect={handleLangChange}
						label={ts("voiceLanguage")}
						value={langOptions.find((o) => o.id === langFilter)}
					/>
					<Dropdown
						floating
						data={genderOptions}
						handleSelect={(item) => setGenderFilter(item.id as string)}
						label={ts("gender")}
						value={genderOptions.find((o) => o.id === genderFilter)}
					/>
					<Dropdown
						floating
						data={ageOptions}
						handleSelect={(item) => setAgeFilter(item.id as string)}
						label={ts("age")}
						value={ageOptions.find((o) => o.id === ageFilter)}
					/>
					<Dropdown
						floating
						data={useCaseOptions}
						handleSelect={(item) => setUseCaseFilter(item.id as string)}
						label={ts("useCase")}
						value={useCaseOptions.find((o) => o.id === useCaseFilter)}
					/>
					<Dropdown
						floating
						data={categoryOptions}
						handleSelect={(item) => setCategoryFilter(item.id as string)}
						label={ts("category")}
						value={categoryOptions.find((o) => o.id === categoryFilter)}
					/>
					<div className="flex flex-col justify-end">
						<SearchField
							className="mt-auto"
							handleChange={(e) => setSearchQuery(e.target.value)}
							handleReset={() => setSearchQuery("")}
							name="voiceSearch"
							placeholder={ts("searchPlaceholder")}
							value={searchQuery}
						/>
					</div>
				</div>

				<VoiceList
					filteredVoices={filteredVoices}
					labelOf={labelOf}
					localSelected={localSelected}
					playingId={playingId}
					ts={ts}
					onPlay={handlePlay}
					onSelect={setLocalSelected}
				/>
			</div>
		</Modal>
	);
}
